import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useParams, useHistory } from 'react-router-dom';
import { RootState, useAppDispatch } from '../../../../store';
import { showFeedbackModal } from '../../../../store/actions/UserFeedbackActions';
import { PiSpinnerGapLight, PiCheckCircleLight, PiWarningCircleLight, PiLightbulbLight, PiLightningLight } from 'react-icons/pi';
import { getMultiHubSocketService } from '../../../../utils/MultiHubSocketService';
import apis from '../../../../utils/apis';
import UnifiedInsightsView from './UnifiedInsightsView';
import InsightViewer from './InsightViewer';
import { useLicenseGate } from '../../../../hooks/useLicenseGate';
import { useGenerateSuggestions } from '../../../../hooks/useGenerateSuggestions';

interface InsightMetadata {
  insight_type_id: string;
  name: string;
  status: 'processing' | 'completed' | 'failed' | 'queued';
  generated_at: string;
  word_count: number;
  version: number;
  error_message?: string | null;
}

export interface AiInsightSuggestion {
  document_type_id: string;
  reason: string;
  priority: 'high' | 'medium' | 'low';
}

interface InsightsTabContentProps {
  matterData: any;
  userType?: string;
  onUpdate?: () => void;
  hasDocuments?: boolean;
}

const InsightsTabContent: React.FC<InsightsTabContentProps> = ({ matterData, userType, onUpdate, hasDocuments = false }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const history = useHistory();
  const { matterId, insightId } = useParams<{ matterId: string; insightId?: string }>();
  const userInfo = useSelector((state: RootState) => state.user.userInfo);
  const { checkAiFeatureGate, hasAiFeatureAccess } = useLicenseGate();

  const [generatingInsightIds, setGeneratingInsightIds] = useState<Set<string>>(new Set());

  // URL-based viewing: insightId from route params determines what we're viewing
  const viewingInsightTypeId = insightId || null;
  const [viewingInsightName, setViewingInsightName] = useState<string>('');
  const [viewingVersionId, setViewingVersionId] = useState<string | undefined>(undefined);
  const [processingMessage, setProcessingMessage] = useState<string>('');
  const [processingProgress, setProcessingProgress] = useState<number>(0);

  // AI suggestions state
  const [aiSuggestions, setAiSuggestions] = useState<AiInsightSuggestion[] | null>(null);

  // Callback to re-fetch suggestions after AI generation completes
  const refetchSuggestions = useCallback(async () => {
    if (!matterData?.uuid) return;
    try {
      const response = await apis.get(`/insights/suggested/${matterData.uuid}`);
      if (response.status === 200) {
        const source = response.data.source || null;
        if (source === 'ai' && response.data.ai_suggestions) {
          const normalizePriority = (p?: string): AiInsightSuggestion['priority'] =>
            p === 'high' || p === 'medium' || p === 'low' ? p : 'low';
          const normalized: AiInsightSuggestion[] = response.data.ai_suggestions.map(
            (s: { document_type_id: string; reason: string; priority?: string }) => ({
              document_type_id: s.document_type_id,
              reason: s.reason,
              priority: normalizePriority(s.priority),
            })
          );
          setAiSuggestions(normalized);
        }
      }
    } catch (err) {
      console.error('[INSIGHTS] Failed to refetch suggestions after generation:', err);
    }
  }, [matterData?.uuid]);

  // Use the async suggestion generation hook (queue → AI engine → PubSub)
  const {
    isGenerating: isGeneratingSuggestions,
    progress: suggestionsProgress,
    handleGenerate: handleGenerateSuggestions,
  } = useGenerateSuggestions({
    matterUuid: matterData?.uuid || '',
    suggestionType: 'insights',
    onComplete: refetchSuggestions,
  });

  const guardedGenerateSuggestions = useCallback(() => {
    if (!hasDocuments) {
      dispatch(showFeedbackModal({ showModal: true, modalType: 'warning', message: t('Upload at least one case file to enable AI generation.'), duration: 3000 }));
      return;
    }
    handleGenerateSuggestions();
  }, [hasDocuments, handleGenerateSuggestions, dispatch, t]);

  const correlationIdRef = useRef<Map<string, string>>(new Map());
  const insightGroupRef = useRef<Map<string, string>>(new Map());
  const refetchInsightsRef = useRef<(() => Promise<void>) | null>(null);
  const pubsubHandlerRef = useRef<((...args: any[]) => void) | null>(null);
  const generatingIdsRef = useRef<Set<string>>(new Set());

  // Keep ref in sync with state for use in event handler
  useEffect(() => {
    generatingIdsRef.current = generatingInsightIds;
  }, [generatingInsightIds]);

  // Hydrate generating state from API on mount — detects items that were
  // already processing before this component mounted (e.g., user navigated away and back)
  useEffect(() => {
    if (!matterData?.uuid) return;

    let pollTimer: ReturnType<typeof setTimeout> | null = null;
    let cancelled = false;

    const checkProcessingItems = async () => {
      try {
        const response = await apis.get(`/insights/list/${matterData.uuid}/merged`);
        if (cancelled) return;

        if (response.status === 200) {
          const documents = response.data.documents || [];
          const processingIds = documents
            .filter((doc: any) => doc.status === 'processing' || doc.status === 'queued')
            .map((doc: any) => doc.document_type_id);

          if (processingIds.length > 0) {
            setGeneratingInsightIds(prev => {
              const next = new Set(prev);
              processingIds.forEach((id: string) => next.add(id));
              return next;
            });
            processingIds.forEach((id: string) => generatingIdsRef.current.add(id));

            // Continue polling while items are processing (every 10s)
            if (!cancelled) {
              pollTimer = setTimeout(checkProcessingItems, 10000);
            }
          } else {
            // No processing items — remove any hydrated IDs that completed
            setGeneratingInsightIds(prev => {
              if (prev.size === 0) return prev;
              // Only remove IDs that don't have a correlation ID (i.e., hydrated, not user-initiated)
              const next = new Set(prev);
              for (const id of prev) {
                if (!correlationIdRef.current.has(id)) {
                  next.delete(id);
                }
              }
              return next;
            });
          }
        }
      } catch (error) {
        console.error('[INSIGHTS] Failed to check processing status:', error);
      }
    };

    checkProcessingItems();

    return () => {
      cancelled = true;
      if (pollTimer) clearTimeout(pollTimer);
    };
  }, [matterData?.uuid]);

  // Fetch suggested insight types from backend (GET only — no auto-generation)
  useEffect(() => {
    if (!matterData?.uuid) return;
    const normalizePriority = (priority?: string): AiInsightSuggestion['priority'] => {
      if (priority === 'high' || priority === 'medium' || priority === 'low') {
        return priority;
      }
      return 'low';
    };
    const fetchSuggested = async () => {
      try {
        const response = await apis.get(`/insights/suggested/${matterData.uuid}`);
        if (response.status === 200) {
          const source = response.data.source || null;
          // Only populate AI suggestions when actually generated by AI, not rule-based fallbacks
          if (source === 'ai' && response.data.ai_suggestions) {
            const normalizedSuggestions: AiInsightSuggestion[] = response.data.ai_suggestions.map(
              (suggestion: { document_type_id: string; reason: string; priority?: string }) => ({
                document_type_id: suggestion.document_type_id,
                reason: suggestion.reason,
                priority: normalizePriority(suggestion.priority)
              })
            );
            setAiSuggestions(normalizedSuggestions);
          }
          // If no AI suggestions exist, user can click "Generate Suggestions" button
        }
      } catch (error) {
        console.error('[INSIGHTS] Failed to fetch suggested insights:', error);
      }
    };
    fetchSuggested();
  }, [matterData?.uuid, matterData?.last_analyzed_at]);

  // Handle insight generation
  const handleGenerateInsight = useCallback(
    async (insightTypeId: string) => {
      if (!checkAiFeatureGate('insights')) return;
      if (!matterData?.uuid) return;
      if (!hasDocuments) {
        dispatch(showFeedbackModal({ showModal: true, modalType: 'warning', message: t('Upload at least one case file to enable AI generation.'), duration: 3000 }));
        return;
      }

      setGeneratingInsightIds(prev => new Set(prev).add(insightTypeId));
      generatingIdsRef.current.add(insightTypeId);

      try {
        const response = await apis.post('/insights/generate', {
          matter_uuid: matterData.uuid,
          insight_type_id: insightTypeId
        });

        if (response.status < 200 || response.status >= 300) {
          throw new Error(`API returned error status: ${response.status}`);
        }

        const { insight_id, pubsub } = response.data;
        correlationIdRef.current.set(insightTypeId, insight_id);
        insightGroupRef.current.set(insightTypeId, pubsub.group);

        // Immediately refresh the merged list to show "Processing" status
        if (refetchInsightsRef.current) {
          await refetchInsightsRef.current();
        }

        const multiHubService = getMultiHubSocketService();
        await multiHubService.connectToHub('ai_processing');
        multiHubService.emit('ai_processing', 'join', { group: pubsub.group });

        // Register a single persistent handler (only once)
        if (!pubsubHandlerRef.current) {
          const handler = (data: any) => {
            handleInsightPubSubEvent(data);
          };
          pubsubHandlerRef.current = handler;
          multiHubService.on('ai_processing', '*', handler);
        }

      } catch (error) {
        console.error('[INSIGHTS] Failed to initiate insight generation:', error);
        setGeneratingInsightIds(prev => {
          const next = new Set(prev);
          next.delete(insightTypeId);
          return next;
        });
        generatingIdsRef.current.delete(insightTypeId);
        dispatch(
          showFeedbackModal({
            showModal: true,
            message: t('Failed to start insight generation'),
            modalType: 'error',
            duration: 5000
          })
        );
      }
    },
    [matterData?.uuid, dispatch, t]
  );

  // Handle pubsub events - single handler for ALL concurrent generations
  const handleInsightPubSubEvent = useCallback(
    (data: any) => {
      const eventData = Array.isArray(data) ? data[0] : data || {};

      // Extract fields from both top-level and nested data object
      const nestedData = eventData.data || {};
      const eventCorrelationId =
        eventData.correlationId ||
        eventData.correlation_id ||
        eventData.CorrelationID;

      // Find which insightTypeId this event belongs to by matching correlation ID
      let matchedInsightTypeId: string | null = null;
      if (eventCorrelationId) {
        for (const [typeId, corrId] of correlationIdRef.current.entries()) {
          if (corrId === eventCorrelationId) {
            matchedInsightTypeId = typeId;
            break;
          }
        }
      }

      // If we can't match to a known insight, skip
      if (!matchedInsightTypeId) {
        return;
      }

      const status = eventData.status?.toLowerCase() || '';
      const subType = eventData.sub_type || nestedData.sub_type || eventData.event_name || eventData.event_type || '';
      const message = eventData.message || eventData.stepName || '';
      const progressPercent = eventData.progressPercent ?? eventData.progress_percent ?? eventData.progress;

      // Track live progress messages
      if (message && status !== 'completed' && status !== 'failed') {
        setProcessingMessage(message);
      }
      if (progressPercent !== undefined && progressPercent !== null) {
        setProcessingProgress(Number(progressPercent));
      }

      // Handle completion
      if (status === 'completed' || subType === 'insight_completed') {
        if (!generatingIdsRef.current.has(matchedInsightTypeId)) return; // Already handled

        setGeneratingInsightIds(prev => {
          const next = new Set(prev);
          next.delete(matchedInsightTypeId!);
          return next;
        });
        generatingIdsRef.current.delete(matchedInsightTypeId);

        // Clear progress if no more generating
        setTimeout(async () => {
          if (generatingIdsRef.current.size === 0) {
            setProcessingMessage('');
            setProcessingProgress(0);
          }
          if (refetchInsightsRef.current) {
            await refetchInsightsRef.current();
          }
        }, 1500);
      }

      // Handle failure
      if (status === 'failed' || subType === 'insight_failed') {
        const errorMessage = nestedData.error || eventData.error_message || eventData.error || eventData.message || t('Unknown error');

        setGeneratingInsightIds(prev => {
          const next = new Set(prev);
          next.delete(matchedInsightTypeId!);
          return next;
        });
        generatingIdsRef.current.delete(matchedInsightTypeId);

        if (generatingIdsRef.current.size === 0) {
          setProcessingMessage('');
          setProcessingProgress(0);
        }

        dispatch(
          showFeedbackModal({
            showModal: true,
            message: t('Insight generation failed'),
            subMsg: errorMessage,
            modalType: 'error',
            duration: 5000
          })
        );

        console.error('[INSIGHTS] Generation failed:', errorMessage);
      }
    },
    [dispatch, t]
  );

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      const multiHubService = getMultiHubSocketService();
      insightGroupRef.current.forEach((group) => {
        multiHubService.emit('ai_processing', 'leave', { group });
      });
      if (pubsubHandlerRef.current) {
        multiHubService.off('ai_processing', '*', pubsubHandlerRef.current);
        pubsubHandlerRef.current = null;
      }
    };
  }, []);

  const handleViewInsight = (insightTypeId: string, insightName: string, versionId?: string) => {
    if (!checkAiFeatureGate('insights')) return;
    setViewingInsightName(insightName);
    setViewingVersionId(versionId);
    history.push(`/matters/${matterId}/insight/${insightTypeId}${versionId ? `?version=${versionId}` : ''}`);
  };

  const handleBackToList = () => {
    setViewingInsightName('');
    setViewingVersionId(undefined);
    history.push(`/matters/${matterId}?tab=Strategy`);
  };

  // Derive the currently-viewed insight's generating state
  const viewingIsGenerating = viewingInsightTypeId ? generatingInsightIds.has(viewingInsightTypeId) : false;

  // Gate: require at least one analysis before showing insights
  // if (!hasBeenAnalyzed) {
  //   return (
  //     <div className="flex flex-col items-center justify-center py-16 text-center rounded-lg">
  //       <div className="h-14 w-14 rounded-full bg-amber-50 dark:bg-amber-900/20 flex items-center justify-center mb-4">
  //         <PiLightningLight className="h-7 w-7 text-amber-500" />
  //       </div>
  //       <h4 className="text-base font-semibold text-gray-900 dark:text-white mb-2">
  //         {t('Analysis Required')}
  //       </h4>
  //       <p className="text-sm text-gray-600 dark:text-gray-400 max-w-sm">
  //         {t('Run an analysis on this matter before generating strategy insights. Upload documents and click the "Analyze" button to get started.')}
  //       </p>
  //     </div>
  //   );
  // }

  // If viewing an insight, show the viewer (gate direct URL access)
  if (viewingInsightTypeId && matterData?.uuid) {
    if (!hasAiFeatureAccess('insights')) {
      // User navigated directly to an insight URL without license — redirect back
      history.replace(`/matters/${matterId}?tab=Strategy`);
      return null;
    }
    return (
      <InsightViewer
        key={`${matterData.uuid}-${viewingInsightTypeId}-${viewingVersionId || 'current'}`}
        matterUuid={matterData.uuid}
        insightTypeId={viewingInsightTypeId}
        insightName={viewingInsightName}
        onBack={handleBackToList}
        onRerun={handleGenerateInsight}
        onGenerate={handleGenerateInsight}
        generatingInsightId={viewingIsGenerating ? viewingInsightTypeId : null}
        initialVersionId={viewingVersionId}
        processingMessage={processingMessage}
        processingProgress={processingProgress}
      />
    );
  }

  return (
    <UnifiedInsightsView
      matterUuid={matterData?.uuid}
      generatingInsightId={generatingInsightIds.size > 0 ? Array.from(generatingInsightIds)[generatingInsightIds.size - 1] : null}
      generatingInsightIds={generatingInsightIds}
      onGenerateInsight={handleGenerateInsight}
      onViewInsight={handleViewInsight}
      onRefresh={() => {
        if (refetchInsightsRef.current) {
          refetchInsightsRef.current();
        }
      }}
      onRefreshReady={(refreshFn) => {
        refetchInsightsRef.current = refreshFn;
      }}
      processingMessage={processingMessage}
      processingProgress={processingProgress}
      aiSuggestions={aiSuggestions}
      isGeneratingSuggestions={isGeneratingSuggestions}
      suggestionsProgress={suggestionsProgress}
      onGenerateSuggestions={guardedGenerateSuggestions}
      hasDocuments={hasDocuments}
      aiFeatureLocked={!hasAiFeatureAccess('insights')}
    />
  );
};

export default InsightsTabContent;
